import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { authApi } from "@/lib/api";
import { toast } from "sonner";
import { IDS } from "@/constants/testIds";
import { User, FileText, Eye, Zap, Users, BookOpen, AlertCircle, Mail } from "lucide-react";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = () => {
    const redirect = window.location.origin + "/callback";
    // Emergent-managed Google Auth: session_id returns in the URL hash fragment.
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  // Passwordless magic-link path — works for any provider (Yahoo, iCloud,
  // Outlook, AOL, business domains) that Google Auth can't accept.
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSendMagicLink = async (e) => {
    e?.preventDefault?.();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      await authApi.magicLinkRequest(trimmed);
      // Backend intentionally returns the same 200 regardless of whether
      // the email exists — we always show "check your inbox".
      setSent(true);
    } catch (err) {
      toast.error(err.message || "Could not send sign-in link. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const features = [
    { icon: User, text: "Primary & Secondary Archetype Results" },
    { icon: FileText, text: "Complete Archetype Profile" },
    { icon: Eye, text: "Field Strengths & Blind Spots" },
    { icon: Zap, text: "Perception & Interpretation Patterns" },
    { icon: Users, text: "Archetype Interactions & Field Dynamics" },
    { icon: BookOpen, text: "Investigation Logging & Export" },
  ];

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center px-4 py-8"
      style={{ background: "var(--pa-background)" }}
      data-testid={IDS.loginPage}
    >
      <div className="w-full max-w-md flex flex-col items-center">
        <div className="w-full max-w-[280px] aspect-[3.5/1] flex items-center justify-center mb-3">
          <img
            src="/images/paranormal-logo.jpg"
            alt="Paranormal Archetypes"
            className="w-full h-full object-contain"
          />
        </div>
        <p
          className="italic text-sm text-center mb-8"
          style={{ color: "var(--pa-text-secondary)" }}
        >
          Alignment precedes breakthrough.
        </p>

        <div className="mb-8 text-center">
          <h1 className="pa-serif text-xl md:text-2xl leading-snug mb-3" style={{ color: "var(--pa-text-primary)" }}>
            Understand how you naturally perceive, interpret, and contribute during paranormal investigations.
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: "var(--pa-text-secondary)" }}>
            A framework for investigator awareness, recognizing your strengths, blind spots, and field patterns.
          </p>
        </div>

        {sent ? (
          <div
            className="pa-card w-full mb-6 p-5 text-center"
            style={{ borderColor: "var(--pa-gold)" }}
            data-testid="login-magic-link-sent"
          >
            <Mail size={22} style={{ color: "var(--pa-gold)", margin: "0 auto 10px" }} />
            <p className="pa-serif text-base mb-2" style={{ color: "var(--pa-gold)" }}>
              Check your inbox
            </p>
            <p className="text-sm mb-3" style={{ color: "var(--pa-text-secondary)" }}>
              If <strong>{email.trim()}</strong> is a valid address, a sign-in link is on its way. It expires in 15 minutes.
            </p>
            <p className="text-xs mb-3" style={{ color: "var(--pa-text-muted)" }}>
              Didn't get it in a minute or two? Check spam. Still nothing?
              <button
                onClick={() => setSent(false)}
                className="underline ml-1"
                style={{ color: "var(--pa-gold)" }}
                data-testid="login-magic-link-try-again"
              >
                Try a different email
              </button>
              .
            </p>
          </div>
        ) : (
          <form onSubmit={handleSendMagicLink} className="w-full mb-4">
            <label className="text-xs font-semibold block mb-2" style={{ color: "var(--pa-gold-light)" }}>
              Sign in with any email
            </label>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pa-input w-full mb-2"
              disabled={sending}
              data-testid="login-magic-link-email-input"
            />
            <button
              type="submit"
              className="pa-btn-gold w-full flex items-center justify-center gap-2"
              disabled={sending}
              data-testid="login-magic-link-send-btn"
            >
              <Mail size={18} />
              {sending ? "Sending..." : "Send me a sign-in link"}
            </button>
            <p className="text-xs mt-2 leading-relaxed" style={{ color: "var(--pa-text-muted)" }}>
              Works with Gmail, Yahoo, Outlook, iCloud, business email — anything. Use the <strong>same email you used at checkout</strong> and your purchase links automatically.
            </p>
          </form>
        )}

        <div className="w-full flex items-center gap-3 my-2" style={{ color: "var(--pa-text-muted)" }}>
          <div style={{ flex: 1, height: 1, background: "var(--pa-card-border)" }} />
          <span className="text-xs">or</span>
          <div style={{ flex: 1, height: 1, background: "var(--pa-card-border)" }} />
        </div>

        <button
          onClick={handleLogin}
          className="pa-btn-ghost w-full flex items-center justify-center gap-2 mb-2"
          data-testid={IDS.loginGoogleBtn}
        >
          <User size={18} />
          Continue with Google
        </button>
        <p className="text-xs mb-6" style={{ color: "var(--pa-text-muted)" }}>
          Optional: Use Google instead if you prefer.
        </p>

        <div
          className="pa-card w-full mb-6 flex items-start gap-3 p-4"
          style={{ borderColor: "var(--pa-gold)" }}
        >
          <AlertCircle size={16} style={{ color: "var(--pa-gold)", marginTop: 2, flexShrink: 0 }} />
          <div>
            <p className="text-xs font-bold mb-1" style={{ color: "var(--pa-gold-light)" }}>
              Using Google sign-in and seeing an &ldquo;Access blocked&rdquo; message?
            </p>
            <p className="text-xs leading-relaxed" style={{ color: "var(--pa-text-secondary)" }}>
              Google does not allow sign in inside in-app browsers like Snapchat, Instagram, Facebook, Messenger, or TikTok. If you opened this from one of those apps, tap the menu and choose &ldquo;Open in browser&rdquo; (Safari or Chrome), then sign in there.
            </p>
          </div>
        </div>

        <p className="text-xs text-center mb-8" style={{ color: "var(--pa-text-muted)" }}>
          Purchase from ParanormalArchetypes.net, then sign in here with the same email. If your purchase doesn&rsquo;t appear right away, tap <strong>&ldquo;I already purchased&rdquo;</strong> on your dashboard.
        </p>

        <div className="pa-card w-full p-5 mb-6">
          <p className="text-sm font-semibold mb-3" style={{ color: "var(--pa-gold)" }}>
            What you&apos;ll discover:
          </p>
          {features.map((f, i) => (
            <div key={i} className="flex items-start gap-3 mb-2">
              <f.icon size={16} color="var(--pa-gold)" style={{ marginTop: 3, flexShrink: 0 }} />
              <span className="text-sm" style={{ color: "var(--pa-text-secondary)" }}>{f.text}</span>
            </div>
          ))}
        </div>

        <p className="text-xs italic text-center px-2 leading-relaxed" style={{ color: "var(--pa-text-muted)" }}>
          Paranormal Archetypes helps you understand how your perceptions, interpretations, and contributions shape your approach to investigation so you can work with greater awareness and intention.
        </p>
      </div>
    </div>
  );
}
